import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  GroupedCheckMetadataItem,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import ImagesComparison from 'dashboard/components/ImagesComparison';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import { CheckName, CompromisedMatch, IdCompromisedDetection } from 'dashboard/models/check';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';

export const computeIdCompromisedDetectionMetadata = (
  check: Omit<IdCompromisedDetection, 'name' | 'requirement'>,
  frontPhotoUrl?: string,
  backPhotoUrl?: string,
): CheckMetadata => {
  const metadataItems: CheckMetadataItem[] = [];

  // Matches are present based on two registries, either fraudulent or publicly available
  // and could indicate either
  if (check.metadata && 'matches' in check.metadata) {
    // Indicate whether match was due to fraudulent or publicly available registry
    const reasonText =
      check.reasons && check.reasons.length > 0
        ? CHECK_DISPLAY_CONFIG[CheckName.IdCompromisedDetection].reasons[check.reasons[0]]
        : 'Matched ID';

    check.metadata.matches?.forEach((match, index) => {
      const comparisonModal = makeImagesComparisonModal(match, frontPhotoUrl, backPhotoUrl);

      metadataItems.push({
        type: CheckMetadataItemType.Group,
        heading: { title: `Match ${index + 1}` },
        items: [
          match['document-front-photo-url'] && {
            label: 'Document Front Photo',
            value: (
              <AsyncImage
                imageUrl={match['document-front-photo-url']}
                width='400px'
                height='250px'
                modalLabel={reasonText}
                modalContents={comparisonModal}
              />
            ),
          },
          match['document-back-photo-url'] && {
            label: 'Document Back Photo',
            value: (
              <AsyncImage
                imageUrl={match['document-back-photo-url']}
                width='400px'
                height='250px'
                modalLabel={reasonText}
                modalContents={comparisonModal}
              />
            ),
          },
        ].filter(Boolean) as GroupedCheckMetadataItem['items'],
      });
    });
  }

  if (check.metadata && 'sample-word' in check.metadata) {
    metadataItems.push({
      type: CheckMetadataItemType.Simple,
      label: 'Sample Word',
      // Sample words are sometimes sensitive, and sample-word is nil (intentionaly hidden) in those cases
      value: check.metadata['sample-word'] || '-',
      labelHelpText: 'The word that was used to detect the compromised ID',
    });
  }

  return {
    metadata: metadataItems,
    hideIfVerificationPassed: false,
  };
};

/**
 * Generates an image comparison between submitted image(s) and matched image(s); activated when
 * the user clicks on the matched image.
 *
 * todo(chris): Consolidate comparison modals here and in other repeat detection functions
 */
function makeImagesComparisonModal(
  match: CompromisedMatch,
  submittedFrontUrl?: string,
  submittedBackUrl?: string,
) {
  const leftImageUrls: string[] = [];
  const leftImageFooterTexts = [];
  const rightImageUrls = [];
  const rightImageFooterTexts = [];

  if (submittedFrontUrl) {
    leftImageUrls.push(submittedFrontUrl);
    leftImageFooterTexts.push('Submitted ID front');
  }

  if (submittedBackUrl) {
    leftImageUrls.push(submittedBackUrl);
    leftImageFooterTexts.push('Submitted ID back');
  }

  if (match['document-front-photo-url']) {
    rightImageUrls.push(match['document-front-photo-url']);
    rightImageFooterTexts.push('Repeat ID front');
  }

  if (match['document-back-photo-url']) {
    rightImageUrls.push(match['document-back-photo-url']);
    rightImageFooterTexts.push('Repeat ID back');
  }

  return (
    <ImagesComparison
      description='Submitted images on left, matched images on right'
      leftImagesUrls={leftImageUrls}
      leftImagesFooterTexts={leftImageFooterTexts}
      rightImagesUrls={rightImageUrls}
      rightImagesFooterTexts={rightImageFooterTexts}
    />
  );
}
